/**
 * Music review console — the queue that lets a submission reach the catalogue.
 *
 * Until this existed, nothing could approve a track except hand-written SQL.
 *
 * ── What a reviewer is actually deciding ──────────────────────────────
 * NOT "is this good music". The question is whether the submitter plausibly
 * holds the rights they claim, and the three things that answer it are laid out
 * side by side on every card:
 *   1. the VERIFIED LEGAL NAME from IDV (we hold it; the public never sees it),
 *   2. the CREDIT they asked for — a mismatch is normal for a stage name and
 *      suspicious for a stranger, which is exactly the judgement a human is here
 *      to make, and
 *   3. the DISCLOSURES, with the shared verdict's blocks and flags computed the
 *      same way the worker computes them.
 *
 * ── The verdict is shown, not re-implemented ──────────────────────────
 * `reviewDisclosures` comes from @postopia/shared — the same function the
 * submission form warns with and the worker enforces with. A console that
 * scored disclosures its own way would eventually disagree with the API that
 * actually decides, and the reviewer would be the last to know.
 *
 * ── Sync is a separate, explicit grant ────────────────────────────────
 * Approving never grants sync implicitly. The checkbox only appears when the
 * shared verdict says it's permissible (both copyrights claimed, samples
 * cleared), and the worker refuses it regardless if that's not true — belt and
 * braces, because "owning a recording is not sync rights" is the mistake this
 * whole pipeline exists to avoid.
 *
 * §0c: no window.confirm anywhere — reject uses an inline reason field, and a
 * reason is required because the submitter reads it.
 *
 * ── Three queues, not one (2026-09-18) ────────────────────────────────
 * TRACKS is the original. Two more were missing entirely:
 *
 * VIDEOS. A music video inserted at status='pending' and nothing anywhere ever
 * set 'live'. Every reader filters 'live', so an artist's video sat pending
 * forever while their studio said "No video yet". It needs its own queue rather
 * than riding the track one because it carries a SECOND rights story: the
 * moving image is its own copyright, and owning the master grants nothing about
 * the footage.
 *
 * CATALOGUE. The takedown surface. music_tracks.status='disabled' was written
 * by nothing at all, so switching a track off, which 512 requires us to do
 * expeditiously, meant hand-written SQL against production. Since the
 * 2026-09-18 founder ruling this is load-bearing rather than tidy: sync now
 * rests on the artist's warranty, and safe harbour is conditional on actually
 * removing.
 */

import { reviewDisclosures } from '@postopia/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { AdminShell } from '../components/AdminShell';
import { currentEnv } from '../lib/envTarget';
import {
  MusicAdminError,
  musicApi,
  type MusicSubmissionRow,
  type MusicTrackRow,
  type MusicVideoRow,
} from '../lib/musicApi';
import { TabBar } from '../components/ui/TabBar';
import { PageShell } from '../components/ui/PageShell';

export const Route = createFileRoute('/music')({ component: MusicRoute });

type Queue = 'submissions' | 'videos' | 'catalogue';

function MusicRoute() {
  const [queue, setQueue] = useState<Queue>('submissions');
  return (
    <AdminShell mode="feature" featureName="Music">
      <PageShell
        title="Music"
        description="Approving publishes to the public catalogue under our DMCA posture. Check the rights story, not the taste."
        maxWidth={860}
      >
        <TabBar
          label="Music console"
          activeKey={queue}
          onSelect={setQueue}
          items={[
            { key: 'submissions', label: 'Tracks' },
            { key: 'videos', label: 'Videos' },
            { key: 'catalogue', label: 'Catalogue' },
          ]}
        />
        {queue === 'submissions' && <MusicConsole />}
        {queue === 'videos' && <VideoConsole />}
        {queue === 'catalogue' && <CatalogueConsole />}
      </PageShell>
    </AdminShell>
  );
}

/** Shared card chrome, so three queues do not drift into three looks. */
const CARD: React.CSSProperties = {
  border: '1px solid var(--c-border-hairline)',
  borderRadius: 8,
  padding: 14,
  marginBottom: 12,
};
const DANGER = 'var(--c-state-danger, #C7563E)';

function btn(kind: 'primary' | 'danger' | 'quiet', disabled: boolean): React.CSSProperties {
  return {
    padding: '8px 14px',
    minHeight: 36,
    borderRadius: 6,
    border: kind === 'quiet' ? `1px solid ${DANGER}` : 'none',
    background: kind === 'danger' ? DANGER : kind === 'quiet' ? 'transparent' : 'var(--c-fg-default)',
    color: kind === 'quiet' ? DANGER : kind === 'danger' ? '#FAF7F2' : 'var(--c-bg-page)',
    opacity: disabled ? 0.45 : 1,
    cursor: disabled ? 'default' : 'pointer',
    fontSize: 13,
  };
}

/**
 * Reason-gated action. §0c: a takedown is irreversible enough to want friction
 * and reversible enough not to want a modal, so the friction is an inline
 * required reason — the same shape the submission reject already uses, and the
 * reason is what the artist and the audit trail both read.
 */
function ReasonAction({
  label,
  placeholder,
  busy,
  onConfirm,
}: {
  label: string;
  placeholder: string;
  busy: boolean;
  onConfirm: (reason: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  if (!open) {
    return (
      <button type="button" disabled={busy} onClick={() => setOpen(true)} style={btn('quiet', busy)}>
        {label}
      </button>
    );
  }
  return (
    <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <input
        type="text"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder={placeholder}
        style={{ padding: '6px 8px', fontSize: 13, minWidth: 260 }}
      />
      <button
        type="button"
        disabled={busy || !reason.trim()}
        onClick={() => onConfirm(reason.trim())}
        style={btn('danger', busy || !reason.trim())}
      >
        Confirm
      </button>
    </span>
  );
}

function MusicConsole() {
  const [rows, setRows] = useState<MusicSubmissionRow[]>([]);
  const [status, setStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    musicApi
      .list(status)
      .then((r) => {
        setRows(r);
        setError(null);
      })
      .catch((e: unknown) =>
        setError(e instanceof MusicAdminError ? `${e.code}: ${e.message}` : String(e)),
      )
      .finally(() => setLoading(false));
  }, [status]);

  useEffect(load, [load]);

  return (
    <>
      <TabBar
        label="Music submission queue"
        activeKey={status}
        onSelect={setStatus}
        items={[
          { key: 'pending', label: 'Pending' },
          { key: 'approved', label: 'Approved' },
          { key: 'rejected', label: 'Rejected' },
        ]}
      />

      {error && <p style={{ color: DANGER, fontSize: 13 }}>{error}</p>}
      {loading ? (
        <p style={{ fontSize: 13, opacity: 0.6 }}>Loading…</p>
      ) : rows.length === 0 ? (
        <p style={{ fontSize: 13, opacity: 0.6 }}>Nothing {status}.</p>
      ) : (
        rows.map((row) => <SubmissionCard key={row.id} row={row} onDone={load} />)
      )}
    </>
  );
}

function SubmissionCard({ row, onDone }: { row: MusicSubmissionRow; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [grantSync, setGrantSync] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);

  // The SAME verdict the form warns with and the worker enforces.
  const verdict = reviewDisclosures(row.disclosures);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      onDone();
    } catch (e) {
      setErr(e instanceof MusicAdminError ? `${e.code}: ${e.message}` : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        border: '1px solid var(--c-border-hairline)',
        borderRadius: 8,
        padding: 14,
        marginBottom: 12,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>
            {row.title}
            {row.is_explicit && <span style={{ fontSize: 11, opacity: 0.6 }}> E</span>}
          </div>
          <div style={{ fontSize: 13, opacity: 0.75 }}>
            credited as <strong>{row.credit_name}</strong>
            {row.credit_url && (
              <>
                {' · '}
                <a href={row.credit_url} target="_blank" rel="noreferrer noopener">
                  link
                </a>
              </>
            )}
          </div>
          {/* The comparison that matters: who they ARE vs who they're crediting.
              A stage name legitimately differs; a stranger's name is the tell. */}
          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
            submitted by @{row.submitter_handle}
            {row.submitter_real_name ? ` — verified as ${row.submitter_real_name}` : ' — no verified name'}
          </div>
          <div style={{ fontSize: 12, opacity: 0.6, marginTop: 2 }}>
            {row.genre ? `${row.genre} · ` : ''}
            {row.isrc ? `ISRC ${row.isrc} · ` : ''}
            {row.release_date ? `${row.release_date} · ` : ''}
            agreement {row.agreement_version}
          </div>
        </div>
      </div>

      {/* Listen. Plain <audio controls> on purpose — a reviewer wants a scrubber
          and a volume control, not our styled player. */}
      {row.r2_key && (
        <audio
          controls
          preload="none"
          src={`/api/v2/music/audio/${row.r2_key}?env=${currentEnv()}`}
          style={{ width: '100%', marginTop: 10 }}
        />
      )}

      {/* Disclosures — every answer, because the reviewer is checking the STORY,
          and a summary would hide the inconsistency that gives a bad one away. */}
      <div style={{ marginTop: 10, fontSize: 12, lineHeight: 1.7 }}>
        {Object.entries(row.disclosures).map(([k, v]) => (
          <span key={k} style={{ marginRight: 12, opacity: v ? 1 : 0.45 }}>
            {k}: <strong>{typeof v === 'boolean' ? (v ? 'yes' : 'no') : (v ?? '—')}</strong>
          </span>
        ))}
      </div>

      {/* Exact duplicate — the fingerprint pipeline's whole reason to exist.
          The API refuses the approve too (409 exact_duplicate); this is the
          reviewer-facing half. A 'pending' fingerprint is called out as
          INCONCLUSIVE — a missing fingerprint must not read as a clean bill. */}
      {row.duplicate_of_track_id ? (
        <p style={{ color: DANGER, fontSize: 13, marginTop: 8, fontWeight: 600 }}>
          Exact duplicate of catalogue track {row.duplicate_of_track_id} — approval
          is refused by the API.
        </p>
      ) : row.duplicate_of_submission_id ? (
        <p style={{ color: DANGER, fontSize: 13, marginTop: 8, fontWeight: 600 }}>
          Same recording as submission {row.duplicate_of_submission_id} (same
          fingerprint or identical bytes) — review the two together.
        </p>
      ) : row.fingerprint_status && row.fingerprint_status !== 'done' ? (
        <p style={{ fontSize: 12, marginTop: 8, opacity: 0.7 }}>
          Fingerprint {row.fingerprint_status} — duplicate check inconclusive.
        </p>
      ) : null}

      {verdict.blocked.length > 0 && (
        <p style={{ color: DANGER, fontSize: 13, marginTop: 8 }}>
          Blocked: {verdict.blocked.join(', ')} — approval is refused by the API.
        </p>
      )}
      {verdict.flags.length > 0 && (
        <p style={{ fontSize: 13, marginTop: 8, opacity: 0.85 }}>
          Look closer: {verdict.flags.join(', ')}
        </p>
      )}

      {err && <p style={{ color: DANGER, fontSize: 13, marginTop: 8 }}>{err}</p>}

      {row.status === 'pending' && (
        <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            disabled={busy || verdict.blocked.length > 0 || !!row.duplicate_of_track_id}
            onClick={() => act(() => musicApi.approve(row.id, { grantSync }))}
            style={{
              padding: '8px 14px',
              minHeight: 36,
              borderRadius: 6,
              border: 'none',
              background: 'var(--c-fg-default)',
              opacity: verdict.blocked.length > 0 || row.duplicate_of_track_id ? 0.45 : 1,
              color: 'var(--c-bg-page)',
              cursor: verdict.blocked.length > 0 || row.duplicate_of_track_id ? 'default' : 'pointer',
              fontSize: 13,
            }}
          >
            Approve
          </button>

          {/* Only offered when the shared verdict allows it — and the worker
              re-checks regardless. */}
          {verdict.syncPermitted && (
            <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={grantSync}
                onChange={(e) => setGrantSync(e.target.checked)}
              />
              also allow use under video (sync)
            </label>
          )}

          {!rejecting ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => setRejecting(true)}
              style={{
                padding: '8px 14px',
                minHeight: 36,
                borderRadius: 6,
                border: `1px solid ${DANGER}`,
                background: 'transparent',
                color: DANGER,
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              Reject…
            </button>
          ) : (
            <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Reason (the submitter sees this)"
                style={{ padding: '6px 8px', fontSize: 13, minWidth: 260 }}
              />
              <button
                type="button"
                disabled={busy || !reason.trim()}
                onClick={() => act(() => musicApi.reject(row.id, reason.trim()))}
                style={{
                  padding: '8px 12px',
                  minHeight: 36,
                  borderRadius: 6,
                  border: 'none',
                  background: DANGER,
                  opacity: reason.trim() ? 1 : 0.45,
                  color: '#FAF7F2',
                  cursor: reason.trim() ? 'pointer' : 'default',
                  fontSize: 13,
                }}
              >
                Confirm reject
              </button>
            </span>
          )}
        </div>
      )}

      {row.status === 'rejected' && row.reject_reason && (
        <p style={{ fontSize: 12, opacity: 0.7, marginTop: 8 }}>Rejected: {row.reject_reason}</p>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
 * MUSIC VIDEO QUEUE
 *
 * What a reviewer is deciding here is NOT what they decided on the track. The
 * recording and the moving image are separate copyrights, so an artist who
 * genuinely owns their master may still hold nothing in the footage: the
 * director may hold the film copyright, and stock or generative clips often bar
 * redistribution outright. The visual-rights answers are therefore shown in
 * full, the same way the track queue shows every disclosure.
 * ═══════════════════════════════════════════════════════════════════════ */

function VideoConsole() {
  const [rows, setRows] = useState<MusicVideoRow[]>([]);
  const [status, setStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    musicApi
      .listVideos(status)
      .then((r) => {
        setRows(r);
        setError(null);
      })
      .catch((e: unknown) =>
        setError(e instanceof MusicAdminError ? `${e.code}: ${e.message}` : String(e)),
      )
      .finally(() => setLoading(false));
  }, [status]);

  useEffect(load, [load]);

  return (
    <>
      <TabBar
        label="Music video queue"
        activeKey={status}
        onSelect={setStatus}
        items={[
          { key: 'pending', label: 'Pending' },
          { key: 'live', label: 'Live' },
          { key: 'rejected', label: 'Rejected' },
          { key: 'disabled', label: 'Disabled' },
        ]}
      />
      {error && <p style={{ color: DANGER, fontSize: 13 }}>{error}</p>}
      {loading ? (
        <p style={{ fontSize: 13, opacity: 0.6 }}>Loading…</p>
      ) : rows.length === 0 ? (
        <p style={{ fontSize: 13, opacity: 0.6 }}>Nothing {status}.</p>
      ) : (
        rows.map((row) => <VideoCard key={row.id} row={row} onDone={load} />)
      )}
    </>
  );
}

function VideoCard({ row, onDone }: { row: MusicVideoRow; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const d = row.disclosures;
  // The one combination the API refuses outright rather than queues: declared
  // third-party footage that is NOT cleared is a takedown waiting to happen,
  // and a reviewer should see that the button is dead before they reach for it.
  const blocked = !!d && d.containsThirdPartyFootage && !d.thirdPartyFootageCleared;

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      onDone();
    } catch (e) {
      setErr(e instanceof MusicAdminError ? `${e.code}: ${e.message}` : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={CARD}>
      <div style={{ fontSize: 15, fontWeight: 600 }}>
        {row.title ?? row.track_title}
        <span style={{ fontSize: 11, opacity: 0.6, fontWeight: 400 }}> · {row.kind}</span>
      </div>
      <div style={{ fontSize: 13, opacity: 0.75 }}>
        for <strong>{row.track_title}</strong>, credited as {row.credit_name}
      </div>
      <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
        {row.artist_handle ? `@${row.artist_handle}` : 'no artist account'}
        {row.artist_real_name ? ` — verified as ${row.artist_real_name}` : ' — no verified name'}
        {row.duration_ms ? ` · ${Math.round(row.duration_ms / 1000)}s` : ''} · agreement{' '}
        {row.agreement_version}
      </div>

      {/* Watch it. Plain <video controls> for the same reason the track queue
          uses a plain <audio>: a reviewer wants a scrubber, not our player. */}
      {row.r2_key && (
        <video
          controls
          preload="none"
          src={`/api/v2/music/audio/${row.r2_key}?env=${currentEnv()}`}
          poster={row.poster_r2_key ? `/api/v2/music/audio/${row.poster_r2_key}?env=${currentEnv()}` : undefined}
          style={{ width: '100%', marginTop: 10, maxHeight: 360, background: '#0E0D0C' }}
        />
      )}

      <div style={{ marginTop: 10, fontSize: 12, lineHeight: 1.7 }}>
        <span style={{ marginRight: 12 }}>
          visualRights: <strong>{row.visual_rights}</strong>
        </span>
        {d &&
          Object.entries(d)
            .filter(([k]) => k !== 'visualRights')
            .map(([k, v]) => (
              <span key={k} style={{ marginRight: 12, opacity: v ? 1 : 0.45 }}>
                {k}: <strong>{typeof v === 'boolean' ? (v ? 'yes' : 'no') : (v ?? '—')}</strong>
              </span>
            ))}
      </div>

      {blocked && (
        <p style={{ color: DANGER, fontSize: 13, marginTop: 8, fontWeight: 600 }}>
          Third-party footage declared and NOT cleared — approval is refused by the API.
        </p>
      )}
      {row.reject_reason && (
        <p style={{ fontSize: 12, opacity: 0.7, marginTop: 8 }}>Rejected: {row.reject_reason}</p>
      )}
      {row.disable_reason && (
        <p style={{ fontSize: 12, opacity: 0.7, marginTop: 8 }}>Disabled: {row.disable_reason}</p>
      )}
      {err && <p style={{ color: DANGER, fontSize: 13, marginTop: 8 }}>{err}</p>}

      <div
        style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}
      >
        {row.status === 'pending' && (
          <>
            <button
              type="button"
              disabled={busy || blocked}
              onClick={() => act(() => musicApi.approveVideo(row.id))}
              style={btn('primary', busy || blocked)}
            >
              Publish
            </button>
            <ReasonAction
              label="Reject…"
              placeholder="Reason (the artist sees this)"
              busy={busy}
              onConfirm={(reason) => act(() => musicApi.rejectVideo(row.id, reason))}
            />
          </>
        )}
        {row.status === 'live' && (
          <ReasonAction
            label="Take down…"
            placeholder="Reason (complaint reference)"
            busy={busy}
            onConfirm={(reason) => act(() => musicApi.disableVideo(row.id, reason))}
          />
        )}
        {row.status === 'disabled' && (
          <button
            type="button"
            disabled={busy}
            onClick={() => act(() => musicApi.enableVideo(row.id))}
            style={btn('primary', busy)}
          >
            Put back
          </button>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
 * CATALOGUE TAKEDOWN
 *
 * The DMCA operational loop. Before this, switching a track off meant
 * hand-written SQL against production by whoever was awake.
 *
 * Two things this surface insists on showing BEFORE the button:
 *
 * 1. `used_by_posts` — how many people's already-published Vibes go silent.
 *    A track is not just a catalogue row once anyone has laid it under their
 *    video, and the person deciding should know the blast radius.
 * 2. `composition_licence_status` — 'asserted' means the sync grant rests on
 *    the ARTIST'S WARRANTY rather than on a clearance we verified (founder
 *    ruling 2026-09-18). When a complaint arrives, that is the single most
 *    useful fact on the row.
 *
 * Disabling is reversible on purpose: we switch a track off while a complaint
 * is open whether or not it proves valid, so 512(g) put-back has a button.
 * ═══════════════════════════════════════════════════════════════════════ */

function CatalogueConsole() {
  const [rows, setRows] = useState<MusicTrackRow[]>([]);
  const [status, setStatus] = useState('active');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    musicApi
      .listTracks(query, status)
      .then((r) => {
        setRows(r);
        setError(null);
      })
      .catch((e: unknown) =>
        setError(e instanceof MusicAdminError ? `${e.code}: ${e.message}` : String(e)),
      )
      .finally(() => setLoading(false));
  }, [query, status]);

  useEffect(load, [load]);

  return (
    <>
      <TabBar
        label="Catalogue status"
        activeKey={status}
        onSelect={setStatus}
        items={[
          { key: 'active', label: 'Live' },
          { key: 'disabled', label: 'Taken down' },
        ]}
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setQuery(q.trim());
        }}
        style={{ display: 'flex', gap: 8, margin: '10px 0 14px' }}
      >
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Title or credited artist"
          style={{ padding: '8px 10px', fontSize: 13, minHeight: 36, flex: 1, minWidth: 0 }}
        />
        <button type="submit" style={btn('primary', false)}>
          Search
        </button>
      </form>

      {error && <p style={{ color: DANGER, fontSize: 13 }}>{error}</p>}
      {loading ? (
        <p style={{ fontSize: 13, opacity: 0.6 }}>Loading…</p>
      ) : rows.length === 0 ? (
        <p style={{ fontSize: 13, opacity: 0.6 }}>No matching tracks.</p>
      ) : (
        rows.map((row) => <TrackCard key={row.id} row={row} onDone={load} />)
      )}
    </>
  );
}

function TrackCard({ row, onDone }: { row: MusicTrackRow; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  // `notified` only comes back from disable; enable returns the plain ok shape.
  const act = async (fn: () => Promise<{ notified?: number } | { ok: boolean }>) => {
    setBusy(true);
    setErr(null);
    try {
      const res = (await fn()) as { notified?: number };
      // Say what the takedown actually did to other people. Silence here would
      // hide the whole reason the collateral count is shown above.
      if (typeof res.notified === 'number') {
        setNote(
          res.notified === 0
            ? 'Taken down. No published Vibes used it.'
            : `Taken down. ${res.notified} Vibe author${res.notified === 1 ? '' : 's'} notified.`,
        );
      }
      onDone();
    } catch (e) {
      setErr(e instanceof MusicAdminError ? `${e.code}: ${e.message}` : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={CARD}>
      <div style={{ fontSize: 15, fontWeight: 600 }}>{row.title}</div>
      <div style={{ fontSize: 13, opacity: 0.75 }}>
        credited as <strong>{row.credit_name}</strong>
        {row.artist_handle ? ` · @${row.artist_handle}` : ''}
      </div>
      <div style={{ fontSize: 12, opacity: 0.6, marginTop: 2 }}>
        {row.licence} · {row.plays_count} plays · track {row.id}
      </div>

      <div style={{ fontSize: 12, marginTop: 8, lineHeight: 1.7 }}>
        <span style={{ marginRight: 12 }}>
          sync: <strong>{row.sync_eligible ? 'yes' : 'no'}</strong>
        </span>
        {row.sync_eligible && (
          <span style={{ marginRight: 12 }}>
            composition:{' '}
            <strong>
              {row.composition_licence_status === 'asserted'
                ? 'artist warranty (not verified by us)'
                : row.composition_licence_status}
            </strong>
          </span>
        )}
        <span style={{ marginRight: 12, color: row.used_by_posts > 0 ? DANGER : undefined }}>
          used by <strong>{row.used_by_posts}</strong> published post
          {row.used_by_posts === 1 ? '' : 's'}
        </span>
      </div>

      {row.disabled_at && (
        <p style={{ fontSize: 12, opacity: 0.7, marginTop: 8 }}>
          Taken down {row.disabled_at}
          {row.disable_reason ? ` — ${row.disable_reason}` : ''}
        </p>
      )}
      {note && <p style={{ fontSize: 13, marginTop: 8 }}>{note}</p>}
      {err && <p style={{ color: DANGER, fontSize: 13, marginTop: 8 }}>{err}</p>}

      <div style={{ marginTop: 12, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {row.status === 'active' ? (
          <ReasonAction
            label="Take down…"
            placeholder="Reason (complaint reference)"
            busy={busy}
            onConfirm={(reason) => act(() => musicApi.disableTrack(row.id, reason))}
          />
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={() => act(() => musicApi.enableTrack(row.id))}
            style={btn('primary', busy)}
          >
            Put back
          </button>
        )}
      </div>
    </div>
  );
}
